import React, { useEffect, useState } from 'react';
import { Box, Text, HStack, VStack, IconButton, Icon, Skeleton, useToast } from '@chakra-ui/react';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import { FiExternalLink, FiCheckCircle, FiX } from 'react-icons/fi';
import { motion, AnimatePresence, useMotionValue, animate } from 'framer-motion';
import { ContentRequirement, SocialChannel } from '@foodfluence/shared';
import { createPortal } from 'react-dom';
import { detectDeliverableTypeFromUrl, detectPlatform, mergeInspirationEntries } from '../../../../utils/urlUtils';
import { getInspirationContentInfo, getContentBriefFileUrl } from '../../../../services/firebaseService';
import { getUgcRateDisplayName } from '../../../../utils/contentUtils';

const INSTAGRAM_CHANNELS: SocialChannel[] = ['instagramReels', 'instagramPosts', 'instagramStories'];

interface ViewGuidelinesSheetProps {
  isOpen: boolean;
  onClose: () => void;
  requirement: ContentRequirement | null;
  clientId?: string;
  clientName?: string;
}

const DRAG_CLOSE_THRESHOLD = 100;

const GuidelineCard: React.FC<{ text: string; variant: 'do' | 'dont' }> = ({ text, variant }) => (
  <Box
    bgGradient={
      variant === 'do'
        ? 'linear(to-b, white, #EEF7EE)'
        : 'linear(to-b, white, #FDEAEA)'
    }
    borderRadius="2xl"
    px={4}
    py={4}
    borderWidth="1px"
    borderColor={variant === 'do' ? '#D1EDD9' : '#F7C5C5'}
    boxShadow="0 1px 3px rgba(0,0,0,0.04)"
  >
    <HStack spacing={3} align="flex-start">
      <Box
        w={2}
        h={2}
        borderRadius="full"
        bg={variant === 'do' ? '#5AAD72' : '#E05252'}
        mt="6px"
        flexShrink={0}
      />
      <Text fontSize="14px" fontWeight="600" color="#0E0E0E" lineHeight="1.6">
        {text}
      </Text>
    </HStack>
  </Box>
);

// ─── Must Include Section ────────────────────────────────────────────────────

const MustIncludeSection: React.FC<{ items: string[] }> = ({ items }) => {
  if (items.length === 0) return null;

  return (
    <VStack align="stretch" spacing={3}>
      <HStack spacing={2} align="center">
        <Text
          fontSize="13px"
          fontWeight="600"
          color="#0E0E0E"
          letterSpacing="0.08em"
          textTransform="uppercase"
        >
          Must Include
        </Text>
        <Box bg="#0E0E0E" borderRadius="full" px={2.5} py={0.5}>
          <Text fontSize="11px" fontWeight="600" color="#C6F432" letterSpacing="0.04em">
            Required
          </Text>
        </Box>
      </HStack>

      <Box bg="#0E0E0E" borderRadius="2xl" overflow="hidden">
        <VStack align="stretch" spacing={0} divider={<Box h="0.5px" bg="rgba(255,255,255,0.06)" />}>
          {items.map((item, i) => (
            <HStack key={i} px={4} py={4} spacing={3} align="center">
              <Icon as={FiCheckCircle} boxSize={5} color="whiteAlpha.800" flexShrink={0} />
              <Text fontSize="14px" color="white" fontWeight="500" lineHeight="1.55" flex={1}>
                {item}
              </Text>
            </HStack>
          ))}
        </VStack>
      </Box>
    </VStack>
  );
};

// ─── Inspiration helpers ──────────────────────────────────────────────────────

const getPlatformIcon = (url: string) => {
  const platform = detectPlatform(url);
  if (platform === 'tiktok') return FaTiktok;
  if (platform === 'instagram') return FaInstagram;
  return FiExternalLink;
};

const getPlatformLabel = (url: string): string => {
  const type = detectDeliverableTypeFromUrl(url);
  switch (type) {
    case 'tiktoks': return 'TikTok';
    case 'instagramReels': return 'Instagram Reel';
    case 'instagramPosts': return 'Instagram Post';
    case 'instagramStories': return 'Instagram Story';
    default: {
      const platform = detectPlatform(url);
      if (platform === 'tiktok') return 'TikTok';
      if (platform === 'instagram') return 'Instagram';
      try { return new URL(url).hostname.replace('www.', ''); } catch { return 'Link'; }
    }
  }
};

const truncateUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    return parsed.hostname.replace('www.', '') + parsed.pathname.slice(0, 20) + (parsed.pathname.length > 20 ? '…' : '');
  } catch {
    return url.slice(0, 30) + (url.length > 30 ? '…' : '');
  }
};

// ─── Media preview card (scraped success) ────────────────────────────────────

const MediaPreviewCard: React.FC<{ url: string; contentType: string }> = ({ url, contentType }) => (
  <Box
    w={{ base: 'calc((100vw - 56px) / 3)', md: '220px' }}
    flexShrink={0}
    borderRadius="2xl"
    overflow="hidden"
    position="relative"
    bg="#F2F2F0"
    cursor="pointer"
    onClick={() => window.open(url, '_blank')}
    boxShadow="0 2px 12px rgba(0,0,0,0.10)"
    sx={{ aspectRatio: '9/16' }}
  >
    {contentType.startsWith('video/') ? (
      <video
        src={`${url}#t=0.001`}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        style={{ objectFit: 'cover', width: '100%', height: '100%', display: 'block' }}
      />
    ) : (
      <img
        src={url}
        alt="Inspiration"
        style={{ objectFit: 'cover', width: '100%', height: '100%', display: 'block' }}
      />
    )}
    {/* Tap overlay hint */}
    <Box
      position="absolute"
      bottom={2}
      right={2}
      bg="rgba(0,0,0,0.45)"
      borderRadius="full"
      w={6}
      h={6}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Icon as={FiExternalLink} boxSize={3} color="white" />
    </Box>
  </Box>
);

// ─── Skeleton placeholder while URL resolves ─────────────────────────────────

const MediaSkeletonCard: React.FC = () => (
  <Skeleton
    w={{ base: 'calc((100vw - 56px) / 3)', md: '120px' }}
    flexShrink={0}
    borderRadius="2xl"
    sx={{ aspectRatio: '9/16' }}
    startColor="#F2F2F0"
    endColor="#E4E4E0"
  />
);

// ─── Link card (fallback for failed/pending scraped or raw inspirationLinks) ──

const PlatformIconBox: React.FC<{ isInstagram: boolean; isTiktok: boolean; PlatformIcon: any }> = ({ isInstagram, isTiktok, PlatformIcon }) => (
  <Box
    w={10}
    h={10}
    borderRadius="xl"
    bg={isInstagram ? '#FFF0F7' : isTiktok ? '#F0F0F0' : '#F2F2F0'}
    display="flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
  >
    {isInstagram ? (
      <Icon
        as={FaInstagram}
        boxSize={5}
        style={{
          background: 'linear-gradient(135deg, #833ab4 0%, #c13584 28%, #e1306c 55%, #f56040 78%, #fcaf45 100%)',
          borderRadius: '50%',
          padding: '2px',
          color: 'white',
        }}
      />
    ) : (
      <Icon as={PlatformIcon} boxSize={5} color={isTiktok ? '#0E0E0E' : '#66666A'} />
    )}
  </Box>
);

const LinkCard: React.FC<{ url: string; w?: any }> = ({ url, w }) => {
  const PlatformIcon = getPlatformIcon(url);
  const label = getPlatformLabel(url);
  const platform = detectPlatform(url);
  const isTiktok = platform === 'tiktok';
  const isInstagram = platform === 'instagram';

  // Narrow/scroll context — vertical card matching media card dimensions
  if (w !== undefined) {
    return (
      <Box
        w={w}
        flexShrink={0}
        bg="white"
        borderRadius="2xl"
        borderWidth={1}
        borderColor="#E4E4E0"
        boxShadow="0 2px 12px rgba(0,0,0,0.07)"
        cursor="pointer"
        onClick={() => window.open(url, '_blank')}
        sx={{ aspectRatio: '9/16' }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        px={2}
        py={3}
        overflow="hidden"
      >
        <VStack spacing={2} align="center">
          <PlatformIconBox isInstagram={isInstagram} isTiktok={isTiktok} PlatformIcon={PlatformIcon} />
          <Text fontSize="12px" fontWeight="600" color="#0E0E0E" textAlign="center">{label}</Text>
          <Text fontSize="12px" color="#66666A" textAlign="center" wordBreak="break-all" lineHeight="1.4">
            {truncateUrl(url)}
          </Text>
          <Icon as={FiExternalLink} boxSize={3.5} color="#B4B4B8" />
        </VStack>
      </Box>
    );
  }

  // Full-width context (VStack of plain inspiration links)
  return (
    <Box
      bg="white"
      borderRadius="2xl"
      borderWidth={1}
      borderColor="#E4E4E0"
      boxShadow="0 2px 12px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.04)"
      px={4}
      py={4}
      cursor="pointer"
      onClick={() => window.open(url, '_blank')}
      _hover={{ boxShadow: '0 4px 16px rgba(0,0,0,0.11)', borderColor: '#B4B4B8' }}
      transition="all 0.15s"
    >
      <HStack spacing={3} justify="space-between">
        <HStack spacing={3}>
          <PlatformIconBox isInstagram={isInstagram} isTiktok={isTiktok} PlatformIcon={PlatformIcon} />
          <VStack align="start" spacing={0}>
            <Text fontSize="14px" fontWeight="600" color="#0E0E0E">{label}</Text>
            <Text fontSize="12px" color="#66666A">{truncateUrl(url)}</Text>
          </VStack>
        </HStack>
        <Icon as={FiExternalLink} boxSize={4} color="#66666A" flexShrink={0} />
      </HStack>
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const ViewGuidelinesSheet: React.FC<ViewGuidelinesSheetProps> = ({ isOpen, onClose, requirement, clientId, clientName }) => {
  const y = useMotionValue(window.innerHeight);
  const toast = useToast();
  const [loadingBrief, setLoadingBrief] = useState(false);

  const briefFilePath = requirement?.contentBriefFile ?? null;
  const briefFileName = briefFilePath?.split('/').pop() ?? null;

  const handleBriefOpen = async () => {
    if (!clientId || !requirement?.id || !briefFileName) return;
    setLoadingBrief(true);
    try {
      const url = await getContentBriefFileUrl(clientId, requirement.id, briefFileName);
      window.open(url, '_blank');
    } catch {
      toast({ title: 'Could not open file', description: 'Please try again.', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setLoadingBrief(false);
    }
  };

  // Pre-fetch Firebase Storage URLs + content-type for scraped success items
  const [resolvedUrls, setResolvedUrls] = useState<Record<string, { url: string; contentType: string }>>({});
  const [failedPaths, setFailedPaths] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen) {
      setResolvedUrls({});
      setFailedPaths(new Set());
      return;
    }

    const successItems = mergeInspirationEntries(
      requirement?.downloadedInspirationContent ?? [],
      requirement?.inspirationLinks ?? [],
    ).filter(item => item.status === 'success' && item.storagePath);

    if (successItems.length === 0) return;

    Promise.allSettled(
      successItems.map(async (item) => {
        const info = await getInspirationContentInfo(item.storagePath!);
        return [item.storagePath!, info] as [string, { url: string; contentType: string }];
      })
    ).then(results => {
      const urlMap: Record<string, { url: string; contentType: string }> = {};
      const failed = new Set<string>();
      results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          urlMap[result.value[0]] = result.value[1];
        } else {
          failed.add(successItems[i].storagePath!);
        }
      });
      setResolvedUrls(urlMap);
      setFailedPaths(failed);
    });
  }, [isOpen, requirement]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen) {
      y.set(window.innerHeight);
      animate(y, 0, { type: 'spring', stiffness: 320, damping: 38 });
    }
  }, [isOpen, y]);

  const handleDragEnd = (_: any, info: { offset: { y: number } }) => {
    if (info.offset.y > DRAG_CLOSE_THRESHOLD) {
      onClose();
    } else {
      animate(y, 0, { type: 'spring', stiffness: 400, damping: 40 });
    }
  };

  const sheetTitle = requirement
    ? `${getUgcRateDisplayName({ type: requirement.type, duration: requirement.duration ?? null, count: requirement.count })} Guidelines`
    : 'Guidelines';

  const guidelines = requirement?.guidelines;
  const dos = guidelines?.dos ?? [];
  const donts = guidelines?.donts ?? [];
  const mustInclude = guidelines?.mustInclude ?? [];
  const additionalInfo = guidelines?.additionalInfo;

  const channels = requirement?.channels ?? [];
  const showSocials = channels.length > 0;

  const getChannelLabel = (channel: SocialChannel): string => {
    switch (channel) {
      case 'instagramReels': return 'Instagram Reel';
      case 'instagramPosts': return 'Instagram Feed';
      case 'instagramStories': return 'Instagram Story';
      case 'tiktoks': return 'TikTok';
    }
  };

  // Inspiration data
  const inspirationLinks: string[] = requirement?.inspirationLinks ?? [];
  const downloadedToShow = mergeInspirationEntries(requirement?.downloadedInspirationContent ?? [], inspirationLinks);
  const hasDownloaded = downloadedToShow.length > 0;
  const showInspirationSection = hasDownloaded || inspirationLinks.length > 0;

  const sheet = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(14,14,14,0.55)',
              zIndex: 1400,
            }}
          />

          {/* Sheet */}
          <motion.div
            key="sheet"
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 38 }}
            drag="y"
            dragConstraints={{ top: 0 }}
            dragElastic={{ top: 0, bottom: 0.25 }}
            style={{
              y,
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              height: '88vh',
              zIndex: 1401,
              background: '#FFFFFF',
              color: '#0E0E0E',
              fontFamily: "'Archivo', system-ui, sans-serif",
              borderTopLeftRadius: '28px',
              borderTopRightRadius: '28px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 -8px 40px rgba(0,0,0,0.18), 0 -2px 8px rgba(0,0,0,0.08)',
              touchAction: 'none',
            }}
            onDragEnd={handleDragEnd}
          >
            {/* Drag handle */}
            <Box pt="10px" pb="4px" display="flex" justifyContent="center" flexShrink={0} cursor="grab">
              <Box aria-hidden="true" w="40px" h="4px" bg="#DCDCD8" borderRadius="999px" />
            </Box>

            {/* Header */}
            <HStack px="20px" py="8px" justify="space-between" align="flex-start" spacing="12px" flexShrink={0}>
              <Text as="h2" textStyle="display" fontSize="27px" lineHeight="1.12" mt="6px">
                {sheetTitle}
              </Text>
              <IconButton
                aria-label="Close"
                icon={<Icon as={FiX} boxSize="18px" color="#0E0E0E" strokeWidth={2.4} />}
                variant="unstyled"
                flex="none"
                borderRadius="999px"
                bg="#F2F2F0"
                w="44px"
                h="44px"
                minW="44px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ bg: '#E4E4E0' }}
                onClick={onClose}
              />
            </HStack>

            {/* Divider */}
            <Box h="1px" bg="#ECECEA" flexShrink={0} />

            {/* Scrollable content */}
            <Box flex={1} overflowY="auto" px={5} pt={6} pb={10} onPointerDownCapture={(e) => e.stopPropagation()} style={{ touchAction: 'pan-y', overscrollBehavior: 'contain' }}>
              <VStack align="stretch" spacing={7}>

                {/* Post to */}
                <Box
                  bg="#F2F2F0"
                  borderRadius="2xl"
                  borderWidth={1}
                  borderColor="#E4E4E0"
                  px={4}
                  py={4}
                >
                  <Text fontSize="13px" fontWeight="600" color="#66666A" letterSpacing="0.08em" textTransform="uppercase" mb={3}>
                    Post to
                  </Text>
                  {showSocials ? (
                    <HStack spacing={2} flexWrap="wrap">
                      {channels.map((channel) => {
                        const isInstagram = INSTAGRAM_CHANNELS.includes(channel);
                        return (
                          <HStack
                            key={channel}
                            spacing={2}
                            px={3}
                            py={2}
                            bg="white"
                            borderRadius="full"
                            borderWidth={1}
                            borderColor="#E4E4E0"
                            boxShadow="0 1px 3px rgba(0,0,0,0.06)"
                          >
                            {isInstagram ? (
                              <Icon
                                as={FaInstagram}
                                boxSize={4}
                                style={{
                                  background: 'linear-gradient(135deg, #833ab4 0%, #c13584 28%, #e1306c 55%, #f56040 78%, #fcaf45 100%)',
                                  borderRadius: '50%',
                                  padding: '2px',
                                  color: 'white',
                                }}
                              />
                            ) : (
                              <Icon as={FaTiktok} boxSize={4} color="#0E0E0E" />
                            )}
                            <Text fontSize="14px" fontWeight="600" color="#3E3E42">{getChannelLabel(channel)}</Text>
                          </HStack>
                        );
                      })}
                    </HStack>
                  ) : (
                    <Text fontSize="14px" color="#66666A" fontWeight="500">
                     No posting to your social platforms required. Upload UGC content only in FoodFluence
                    </Text>
                  )}
                </Box>

                {/* Inspiration */}
                {showInspirationSection && (
                  <VStack align="stretch" spacing={3}>
                    <Text
                      fontSize="13px"
                      fontWeight="600"
                      color="#66666A"
                      letterSpacing="0.08em"
                      textTransform="uppercase"
                    >
                      Inspiration
                    </Text>

                    {hasDownloaded ? (
                      <HStack spacing={2} overflowX="auto" pb={1} mx={-1} px={1} align="stretch">
                        {downloadedToShow.map((item, i) => {
                          const isSuccess = item.status === 'success' && !!item.storagePath;
                          const cardW = { base: 'calc((100vw - 56px) / 3)', md: '220px' };
                          if (isSuccess) {
                            const path = item.storagePath!;
                            if (failedPaths.has(path)) return <LinkCard key={i} url={item.originalUrl} w={cardW} />;
                            const resolved = resolvedUrls[path];
                            if (resolved) return <MediaPreviewCard key={i} url={resolved.url} contentType={resolved.contentType} />;
                            return <MediaSkeletonCard key={i} />;
                          }
                          return <LinkCard key={i} url={item.originalUrl} w={cardW} />;
                        })}
                      </HStack>
                    ) : (
                      <VStack align="stretch" spacing={2}>
                        {inspirationLinks.map((url, i) => (
                          <LinkCard key={i} url={url} />
                        ))}
                      </VStack>
                    )}
                  </VStack>
                )}

                {/* Must Include */}
                <MustIncludeSection items={mustInclude} />

                {/* Do's */}
                {dos.length > 0 && (
                  <VStack align="stretch" spacing={3}>
                    <Text fontSize="13px" fontWeight="600" color="#66666A" letterSpacing="0.08em" textTransform="uppercase">
                      Do's
                    </Text>
                    {dos.map((item, i) => (
                      <GuidelineCard key={i} text={item} variant="do" />
                    ))}
                  </VStack>
                )}

                {/* Don'ts */}
                {donts.length > 0 && (
                  <VStack align="stretch" spacing={3}>
                    <Text fontSize="13px" fontWeight="600" color="#66666A" letterSpacing="0.08em" textTransform="uppercase">
                      Don'ts
                    </Text>
                    {donts.map((item, i) => (
                      <GuidelineCard key={i} text={item} variant="dont" />
                    ))}
                  </VStack>
                )}

                {/* Content Brief */}
                {briefFileName && (
                  <VStack align="stretch" spacing={3}>
                    <Text fontSize="13px" fontWeight="600" color="#66666A" letterSpacing="0.08em" textTransform="uppercase">
                      Content Brief
                    </Text>
                    <Box
                      bg="white"
                      borderRadius="2xl"
                      borderWidth={1}
                      borderColor="#E4E4E0"
                      boxShadow="0 2px 12px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.04)"
                      px={4}
                      py={4}
                      cursor="pointer"
                      onClick={handleBriefOpen}
                      _hover={{ boxShadow: '0 4px 16px rgba(0,0,0,0.11)', borderColor: '#B4B4B8' }}
                      transition="all 0.15s"
                    >
                      <HStack spacing={3} justify="space-between" minW={0}>
                        <HStack spacing={3} minW={0} flex={1}>
                          <Box
                            w={10}
                            h={10}
                            borderRadius="xl"
                            bg="#F2F2F0"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                          >
                            <Icon as={FiExternalLink} boxSize={5} color="#66666A" />
                          </Box>
                          <VStack align="start" spacing={0} minW={0} flex={1}>
                            <Text fontSize="14px" fontWeight="600" color="#0E0E0E">
                              {clientName ? `${clientName} Content Brief` : 'Content Brief'}
                            </Text>
                          </VStack>
                        </HStack>
                        {loadingBrief
                          ? <Skeleton w={4} h={4} borderRadius="sm" />
                          : <Icon as={FiExternalLink} boxSize={4} color="#66666A" flexShrink={0} />
                        }
                      </HStack>
                    </Box>
                  </VStack>
                )}

                {/* Additional Info */}
                {additionalInfo && (
                  <VStack align="stretch" spacing={2}>
                    <Text fontSize="13px" fontWeight="600" color="#66666A" letterSpacing="0.08em" textTransform="uppercase">
                      Additional Info
                    </Text>
                    <Text fontSize="14px" color="#3E3E42" lineHeight="1.75">
                      {additionalInfo}
                    </Text>
                  </VStack>
                )}

              </VStack>
            </Box>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(sheet, document.body);
};

export default ViewGuidelinesSheet;
